/**
 * Display-only funding estimate multipliers for the homepage eligibility
 * calculator. Not used for real underwriting.
 *
 * The multiple is driven by monthly revenue, banded by segment:
 *
 * | Segment          | Monthly revenue      | Multiple  |
 * | ---------------- | -------------------- | --------- |
 * | Other businesses | any                  | 1–1.2x    |
 * | Consumer brands  | < 125k               | 1–2x      |
 * | Consumer brands  | 125k – 400k          | 1.5–2.5x  |
 * | Consumer brands  | >= 400k              | 1.5–3x    |
 *
 * Each category is then nudged a little way inside its band (see
 * {@link CATEGORY_VARIATION_FRACTION}) so two businesses of the same size in
 * different categories don't see an identical range. The nudge only ever moves
 * a bound *inwards*, so no estimate falls outside the band above.
 */

import { FUNDING_ELIGIBILITY_CATEGORY_GROUPS } from "@/lib/apply-now/funding-eligibility-categories";

export interface FundingEligibilityCategoryMultipliers {
  lower: number;
  upper: number;
}

export type FundingEligibilitySegment = "consumer-brands" | "other-businesses";

const CONSUMER_BRANDS_GROUP_LABEL = "CONSUMER BRANDS";

/**
 * Derived from the canonical category groups rather than duplicated, so a
 * category moved between groups can never fall out of sync with the dropdown.
 */
const CONSUMER_BRAND_CATEGORIES: ReadonlySet<string> = new Set(
  FUNDING_ELIGIBILITY_CATEGORY_GROUPS.find(
    (group) => group.label === CONSUMER_BRANDS_GROUP_LABEL
  )?.options.map((option) => option.value) ?? []
);

/**
 * Unrecognised and empty categories fall to "other businesses" — the narrower,
 * lower band. An estimate is only rendered once a category is chosen, so this
 * is a safety net rather than a real path, and it errs low rather than
 * over-promising.
 */
export function getFundingEligibilitySegment(
  category: string
): FundingEligibilitySegment {
  return CONSUMER_BRAND_CATEGORIES.has(category.trim())
    ? "consumer-brands"
    : "other-businesses";
}

const OTHER_BUSINESSES_BAND: FundingEligibilityCategoryMultipliers = {
  lower: 1,
  upper: 1.2,
};

/**
 * Consumer-brand bands, ordered low to high. `belowRevenue` is the exclusive
 * upper edge in USD-equivalent monthly revenue; the last band catches the rest.
 *
 * The brief left the exact 400k boundary open ("< 400k" and "> 400k"); it is
 * treated as the start of the top band so no revenue is unhandled.
 */
const CONSUMER_BRAND_BANDS: readonly (FundingEligibilityCategoryMultipliers & {
  belowRevenue: number;
})[] = [
  { belowRevenue: 125_000, lower: 1, upper: 2 },
  { belowRevenue: 400_000, lower: 1.5, upper: 2.5 },
  { belowRevenue: Number.POSITIVE_INFINITY, lower: 1.5, upper: 3 },
];

/**
 * How far into its band a category may be nudged, per bound.
 *
 * Proportional to band width, but with a floor: the 1–1.2x band is so narrow
 * that 12% of it lands well inside one rounding step, so every "other
 * business" would render the identical range. The floor is expressed in
 * multiplier points (0.05x of monthly revenue) because that is what has to
 * clear a rounding step, and it is capped at a quarter of the band so a
 * category can never be squeezed into the middle of a wide band.
 */
const CATEGORY_VARIATION_FRACTION = 0.12;
const CATEGORY_VARIATION_FLOOR = 0.05;
const CATEGORY_VARIATION_CEILING_FRACTION = 0.25;

function categoryVariationInset(bandWidth: number): number {
  return Math.min(
    Math.max(bandWidth * CATEGORY_VARIATION_FRACTION, CATEGORY_VARIATION_FLOOR),
    bandWidth * CATEGORY_VARIATION_CEILING_FRACTION
  );
}

/**
 * Currencies whose figures live roughly an order of magnitude away from USD,
 * so the revenue bands need scaling rather than reusing the literal digits.
 * Matches the treatment of the advance cap in `funding-eligibility-shared.ts`
 * (~7.5 DKK and ~11 SEK per EUR, both rounded to 10 to keep bands round).
 */
const CURRENCY_BAND_SCALE: Partial<Record<string, number>> = {
  DKK: 10,
  SEK: 10,
};

export function fundingEligibilityBandScale(currencyCode: string): number {
  return CURRENCY_BAND_SCALE[currencyCode] ?? 1;
}

/**
 * Stable hash in [0, 1). Deterministic so a category always renders the same
 * figure — a random nudge would differ between server and client and trip
 * hydration, and would change under the user on re-render.
 */
function categoryVariation(category: string, bound: string): number {
  let hash = 0x811c9dc5;
  for (const character of `${category}:${bound}`) {
    hash ^= character.codePointAt(0)!;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash / 0x100000000;
}

function bandFor(
  category: string,
  monthlyRevenue: number,
  currencyCode: string
): FundingEligibilityCategoryMultipliers {
  if (getFundingEligibilitySegment(category) === "other-businesses") {
    return OTHER_BUSINESSES_BAND;
  }

  const scale = fundingEligibilityBandScale(currencyCode);
  const { lower, upper } = CONSUMER_BRAND_BANDS.find(
    ({ belowRevenue }) => monthlyRevenue < belowRevenue * scale
  )!;

  return { lower, upper };
}

/**
 * Multiplier range for a category at a given monthly revenue, including the
 * per-category nudge. Both bounds stay within the segment's published band.
 */
export function getFundingEligibilityMultipliers(
  category: string,
  monthlyRevenue: number,
  currencyCode = "USD"
): FundingEligibilityCategoryMultipliers {
  const band = bandFor(category, monthlyRevenue, currencyCode);
  const inset = categoryVariationInset(band.upper - band.lower);

  return {
    lower: band.lower + categoryVariation(category, "lower") * inset,
    upper: band.upper - categoryVariation(category, "upper") * inset,
  };
}

/** The published band for a segment and revenue, before the per-category nudge. */
export function getFundingEligibilityBand(
  category: string,
  monthlyRevenue: number,
  currencyCode = "USD"
): FundingEligibilityCategoryMultipliers {
  return bandFor(category, monthlyRevenue, currencyCode);
}
